import { clsx, type ClassValue } from 'clsx';
import { createElement, type ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Convert **bold** markdown to Unicode Mathematical Sans-Serif Bold characters.
 * These render as visually bold on LinkedIn, Facebook, Twitter, and most platforms.
 */
export function markdownBoldToUnicode(text: string): string {
  const BOLD_OFFSET_UPPER = 0x1D5D4 - 0x41; // 𝗔 - A
  const BOLD_OFFSET_LOWER = 0x1D5EE - 0x61; // 𝗮 - a
  const BOLD_OFFSET_DIGIT = 0x1D7EC - 0x30; // 𝟬 - 0

  const toBoldChar = (ch: string): string => {
    const code = ch.charCodeAt(0);
    if (code >= 0x41 && code <= 0x5A) return String.fromCodePoint(code + BOLD_OFFSET_UPPER);
    if (code >= 0x61 && code <= 0x7A) return String.fromCodePoint(code + BOLD_OFFSET_LOWER);
    if (code >= 0x30 && code <= 0x39) return String.fromCodePoint(code + BOLD_OFFSET_DIGIT);
    return ch;
  };

  return text.replace(/\*\*(.+?)\*\*/gs, (_, inner: string) =>
    [...inner].map(toBoldChar).join('')
  );
}

/**
 * Convert **bold** markdown to React <strong> elements for in-app rendering.
 * Returns an array of ReactNodes that can be spread directly into JSX.
 */
export function renderMarkdownBold(text: string): ReactNode[] {
  const parts: ReactNode[] = [];
  const regex = /\*\*(.+?)\*\*/g;
  let lastIndex = 0;
  let match;
  let key = 0;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(createElement('strong', { key: `bold-${key++}`, className: 'font-semibold' }, match[1]));
    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
}
